import React, { useState, useCallback } from "react";
import { connect } from "react-redux";

import {
  Dropdown,
  DropdownMenu,
  IconButton,
  IconDisclosure,
} from "@toddle-edu/ds-web";
import {
  AddOutlined,
  MultipleUsersOutlined,
  FamilyOutlined,
  ChatTextOutlined,
  GlobeOutlined,
  ChatLeftDotsOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";

import {
  CREATE_CHANNEL_CATEGORY,
  CHAT_TYPE,
  isCreateAndManageOwnChannelEnabled,
  isInitiateMessageEnabled,
  isMessagingPublicChannelEnabled,
  isMessagingDepartmentChannelEnabled,
  isMessagingV2ChannelEnabled,
} from "MessagingV2/modules/Utils";
import {
  getMessagingV2ReduxState,
  goToPublicChannelsRoute,
  setCreateChannelDropdownOpen,
} from "MessagingV2/modules/MessagingModule";

import CreateChannelModal from "MessagingV2/components/Channels/CreateChannelModal";
import CreateFamilyChannelModal from "MessagingV2/components/Channels/CreateFamilyChannelModal";
import CreateNewChatModal from "MessagingV2/components/ThreadList/CreateNewChatModal";
import { CreateDepartmentChannelModal } from "MessagingV2/components/Channels/CreateDepartmentChannel";

import classes from "./InitChatAndChannelButton.scss";
import _ from "lodash";
import { getIsParent } from "modules/CommonSelectors";

const styles = {
  dropdownIconStyle: { alignSelf: "flex-start" },
  itemTitleContainerStyle: { width: 400 },
  createIconDisclosureStyle: { height: "32px" },
  shortcutCode: { code: "KeyC" },
};

const getCreateChatAndChannelOptions = ({ t }) => {
  const options = [];
  const showInitiateChat = isInitiateMessageEnabled();

  const showPublicChannel = isMessagingPublicChannelEnabled();
  const isChannelEnabled = isMessagingV2ChannelEnabled();

  if (showInitiateChat) {
    options.push({
      key: CHAT_TYPE.CHAT,
      label: t("common:chat"),
      subText: (
        <div className={classes.dropdownSubText}>
          {t("common:chat_create_description")}
        </div>
      ),
      icon: (
        <div style={styles.dropdownIconStyle}>
          <ChatTextOutlined variant="subtle" />
        </div>
      ),
    });
  }

  if (isChannelEnabled) {
    options.push(
      {
        key: CREATE_CHANNEL_CATEGORY.FAMILY_CHANNEL,
        label: t("common:family_channel"),
        subText: (
          <div className={classes.dropdownSubText}>
            {t("common:family_channel_create_description")}
          </div>
        ),
        icon: (
          <div style={styles.dropdownIconStyle}>
            <FamilyOutlined variant="subtle" />
          </div>
        ),
      },
      {
        key: CREATE_CHANNEL_CATEGORY.CUSTOM_CHANNEL,
        label: t("common:custom_channel"),
        subText: (
          <div className={classes.dropdownSubText}>
            {t("common:custom_channel_create_description")}
          </div>
        ),
        icon: (
          <div style={styles.dropdownIconStyle}>
            <MultipleUsersOutlined variant="subtle" />
          </div>
        ),
      }
    );
  }

  if (showPublicChannel && isChannelEnabled) {
    options.push({
      key: CREATE_CHANNEL_CATEGORY.PUBLIC_CHANNEL,
      label: t("common:browse_public_channels"),
      subText: (
        <div className={classes.dropdownSubText}>
          {t("common:browse_public_channel_description")}
        </div>
      ),
      icon: (
        <div style={styles.dropdownIconStyle}>
          <GlobeOutlined variant="subtle" />
        </div>
      ),
    });
  }

  return options;
};

const getInitChatChannelOptionWithoutChannelCreate = ({ t }) => {
  const options = [];
  const showInitiateChat = isInitiateMessageEnabled();
  const isPublicChannelEnabled = isMessagingPublicChannelEnabled();
  const isDepartmentChannelEnabled = isMessagingDepartmentChannelEnabled();
  const isChannelEnabled = isMessagingV2ChannelEnabled();

  const isParent = getIsParent();

  if (showInitiateChat) {
    options.push({
      key: CHAT_TYPE.CHAT,
      label: t("common:chat"),
      subText: (
        <div className={classes.dropdownSubText}>
          {t("common:start_conversation_with_staff")}
        </div>
      ),
      icon: (
        <div style={styles.dropdownIconStyle}>
          <ChatTextOutlined variant="subtle" />
        </div>
      ),
    });
  }

  if (isDepartmentChannelEnabled && isParent && isChannelEnabled) {
    options.push({
      key: CREATE_CHANNEL_CATEGORY.DEPARTMENT_CHANNEL,
      label: t("common:department_channel"),
      subText: (
        <div className={classes.dropdownSubText}>
          {t("common:start_conversation_with_department")}
        </div>
      ),
      icon: (
        <div style={styles.dropdownIconStyle}>
          <ChatLeftDotsOutlined variant="subtle" />
        </div>
      ),
    });
  }

  if (isPublicChannelEnabled && isChannelEnabled) {
    options.push({
      key: CREATE_CHANNEL_CATEGORY.PUBLIC_CHANNEL,
      label: t("common:browse_public_channels"),
      subText: (
        <div className={classes.dropdownSubText}>
          {t("common:browse_public_channel_description")}
        </div>
      ),
      icon: (
        <div style={styles.dropdownIconStyle}>
          <GlobeOutlined variant="subtle" />
        </div>
      ),
    });
  }

  return options;
};

const InitChatAndChannelButton = props => {
  const {
    createChannelDropdownOpen,
    setCreateChannelDropdownOpen,
    isSearchMode,
    testId,
  } = props;
  const { t } = useI18n(["common"]);

  const dispatch = useDispatch();

  const channelOptions = getCreateChatAndChannelOptions({
    t,
  });

  const [createChatType, setCreateChatType] = useState(null);

  const canCreateChannel =
    isMessagingV2ChannelEnabled() && isCreateAndManageOwnChannelEnabled();

  const onCloseCreateModal = useCallback(() => {
    setCreateChatType(null);
  }, []);

  const onClickCreate = useCallback(({ key }) => {
    if (_.isEqual(key, CREATE_CHANNEL_CATEGORY.PUBLIC_CHANNEL)) {
      setCreateChannelDropdownOpen(false);
      dispatch(goToPublicChannelsRoute());
    }

    setCreateChatType(key);
  }, []);

  const onAddNewChat = useCallback(() => {
    setCreateChatType(CHAT_TYPE.CHAT);
  }, []);

  if (isSearchMode) return null;

  const showInitiateChat = isInitiateMessageEnabled();

  const isPublicChannelEnabled = isMessagingPublicChannelEnabled();
  const isDepartmentChannelEnabled = isMessagingDepartmentChannelEnabled();

  const showDropdownWithoutCreateChannel =
    isMessagingV2ChannelEnabled() &&
    (isPublicChannelEnabled || isDepartmentChannelEnabled);

  const renderDropdown = options => (
    <Dropdown
      overlay={
        <DropdownMenu
          options={options}
          onClick={onClickCreate}
          dsVersion="2.0"
          onShortcutPress={onClickCreate}
          className={classes.dropdownMenu}
          testId={testId}
        />
      }
      visible={createChannelDropdownOpen}
      onVisibleChange={setCreateChannelDropdownOpen}
      itemTitleContainerStyle={styles.itemTitleContainerStyle}
    >
      <IconDisclosure
        icon={<AddOutlined />}
        shortcut={styles.shortcutCode}
        dsVersion="2.0"
        size="medium"
        variant="primary"
        testId={`${testId}-initiate`}
      />
    </Dropdown>
  );

  return (
    <div className={classes.rootContainer}>
      {canCreateChannel && renderDropdown(channelOptions)}
      {!canCreateChannel &&
        showDropdownWithoutCreateChannel &&
        renderDropdown(getInitChatChannelOptionWithoutChannelCreate({ t }))}
      {!canCreateChannel &&
        !showDropdownWithoutCreateChannel &&
        showInitiateChat && (
          <IconButton
            icon={<AddOutlined />}
            size="medium"
            style={styles.initiateButtonStyle}
            shortcut={styles.shortcutCode}
            dsVersion="2.0"
            onClick={onAddNewChat}
            variant="primary"
            testId={`${testId}-initiate`}
          />
        )}

      {_.isEqual(createChatType, CHAT_TYPE.CHAT) ? (
        <CreateNewChatModal onCloseModal={onCloseCreateModal} />
      ) : null}

      {_.isEqual(createChatType, CREATE_CHANNEL_CATEGORY.CUSTOM_CHANNEL) ? (
        <CreateChannelModal onCloseModal={onCloseCreateModal} />
      ) : null}
      {_.isEqual(createChatType, CREATE_CHANNEL_CATEGORY.FAMILY_CHANNEL) ? (
        <CreateFamilyChannelModal onCloseModal={onCloseCreateModal} />
      ) : null}
      {_.isEqual(createChatType, CREATE_CHANNEL_CATEGORY.DEPARTMENT_CHANNEL) ? (
        <CreateDepartmentChannelModal onCloseModal={onCloseCreateModal} />
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const messagingState = getMessagingV2ReduxState(state);

  const { searchFilter, isSearchFocused } = _.get(
    messagingState,
    "messageRevampV2FilterState",
    {}
  );

  const isSearchMode = isSearchFocused || !_.isEmpty(searchFilter);

  return {
    createChannelDropdownOpen: _.get(
      messagingState,
      "createChannelDropdownOpen",
      false
    ),
    isSearchMode,
  };
};

const mapActionCreators = {
  setCreateChannelDropdownOpen,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(InitChatAndChannelButton);
